import React from 'react';
import {styled} from '@volkswagen-onehub/components-core';

import {TabPagination} from './tab-pagination';
import {resetButtonStyles, StyledAccessibleLabel} from '../helpers';
import {RenderPaginationProps} from './types';

export const BULLET_BUTTON_HEIGHT = '4px';
export const BULLET_BUTTON_PADDING = '5px';

// NOTE: the expected behaviour for RTL users for carousel components is 'rtl' direction on desktop and 'ltr' on mobile devices. We currently force 'ltr' for all viewports and should adapt this to the correct expected behaviour in the future. We will start with the Stage Slider for which 'enableRtl' was introduced.
const StyledBulletWrapper = styled.div<{enableRtl?: boolean}>`
    ${props => !props.enableRtl && 'direction: ltr;'}
    padding-block-start: ${props => props.theme.size.dynamic0100};
    text-align: center;
    /* NOTE: added due cutter focus outline in mood gallery / editorial teaser section on mobile in carousel */
    padding-bottom: 2px;
`;

export const StyledBulletButton = styled.button<{isActive?: boolean}>`
    ${resetButtonStyles};
    display: inline-block;
    position: relative;
    padding: ${BULLET_BUTTON_PADDING};

    &::before {
        content: '';
        display: block;
        height: ${BULLET_BUTTON_HEIGHT};
        width: ${BULLET_BUTTON_HEIGHT};
        border-radius: 50%;
        background-color: ${props => props.theme.colors.button.primary.default};
        transition: transform 0.2s;
        ${props => (props.isActive ? 'transform: scale(2);' : '')};
    }

    &:focus {
        outline: none;

        &::before {
            transform: scale(1.6);
            ${props =>
                props.isActive
                    ? 'transform: scale(2);'
                    : 'transform: scale(1.6);'};
        }

        &::after {
            content: '';
            display: block;
            position: absolute;
            inset: 0;
            border-radius: 50%;
            border: solid 2px ${props => props.theme.colors.focus.main};
            margin: -2px;
        }
    }
`;

export const BulletPagination = (
    props: RenderPaginationProps
): React.ReactElement => {
    const {
        paginationData,
        activeIndex,
        paginationLabel,
        enableRtl = false,
        altTextList
    } = props;
    const wrapperRef = React.useRef<HTMLDivElement>(null);

    return (
        <TabPagination {...props} ref={wrapperRef}>
            {({onKeyDown, onBulletClick}) => (
                <StyledBulletWrapper
                    role="tablist"
                    aria-label={paginationLabel}
                    onKeyDown={onKeyDown}
                    ref={wrapperRef}
                    enableRtl={enableRtl}
                >
                    {paginationData.map(({label, id, panelId}, index) => {
                        const labelText = altTextList?.[index] ?? undefined;
                        const isActive = index === activeIndex;
                        return (
                            <StyledBulletButton
                                isActive={isActive}
                                onClick={onBulletClick(index)}
                                key={`bullet-${index}`}
                                role="tab"
                                id={id}
                                aria-controls={panelId}
                                tabIndex={isActive ? 0 : -1}
                                aria-selected={isActive}
                                aria-label={labelText}
                            >
                                <StyledAccessibleLabel>
                                    {label}
                                </StyledAccessibleLabel>
                            </StyledBulletButton>
                        );
                    })}
                </StyledBulletWrapper>
            )}
        </TabPagination>
    );
};
